<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useParticipants } from '@/composables/useParticipants'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import { useRaces } from '@/composables/useRaces'
import { getRaceParticipantsExportUrl } from '@/api/participants'
import type { RegistrationStatus } from '@/types/participants'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ExportCsvLink from '@/components/shared/ExportCsvLink.vue'
import ParticipantTable from '@/components/organizer/participants/ParticipantTable.vue'

const { t } = useI18n()
const route = useRoute()
const { participants, paginationMeta, loading, error, fetchRaceParticipants, bulkPromoteRegistrations } = useParticipants()
const { event, fetchEvent } = useEvents()
const { edition, fetchEdition } = useEditions()
const { race, fetchRace } = useRaces()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string
const raceSlug = route.params['raceSlug'] as string

const currentFilters = ref<{
  status: RegistrationStatus | null
  search: string
  page: number
  per_page: number
}>({ status: null, search: '', page: 1, per_page: 25 })

const exportUrl = computed(() => {
  if (!race.value) return ''
  return getRaceParticipantsExportUrl(race.value.id, {
    status: currentFilters.value.status,
    search: currentFilters.value.search || undefined,
  })
})

function onFilter(filters: { status: RegistrationStatus | null; search: string; page: number; per_page: number }): void {
  if (!race.value) return
  currentFilters.value = filters
  void fetchRaceParticipants(race.value.id, {
    status: filters.status,
    search: filters.search || undefined,
    page: filters.page,
    per_page: filters.per_page,
  })
}

async function handleBulkPromote(ids: number[]): Promise<void> {
  if (!race.value) return
  await bulkPromoteRegistrations(ids)
  void fetchRaceParticipants(race.value.id, {
    status: currentFilters.value.status,
    search: currentFilters.value.search || undefined,
    page: currentFilters.value.page,
    per_page: currentFilters.value.per_page,
  })
}

onMounted(async () => {
  await Promise.all([
    fetchEvent(eventSlug),
    fetchEdition(eventSlug, editionSlug),
    fetchRace(eventSlug, editionSlug, raceSlug),
  ])
  if (race.value) {
    void fetchRaceParticipants(race.value.id)
  }
})
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${raceSlug}/participants`" class="hover:text-slate-300 transition-colors">
          <template v-if="race">{{ race.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">
          {{ t('nav.participants') }}
        </span>
      </nav>

      <!-- Heading -->
      <div class="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 class="text-3xl font-bold text-white">
            {{ event?.name ?? '' }} · {{ race?.name ?? '' }}
          </h1>
          <p class="text-slate-400 mt-1">{{ t('participants.race_title') }}</p>
        </div>
        <ExportCsvLink v-if="exportUrl" :href="exportUrl" class="shrink-0">
          {{ t('participants.export_csv') }}
        </ExportCsvLink>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t(error) }}
      </div>

      <!-- Table -->
      <ParticipantTable
        :participants="participants"
        :pagination-meta="paginationMeta"
        :loading="loading"
        :show-race-column="false"
        :on-bulk-promote="handleBulkPromote"
        @filter="onFilter"
      />
    </main>
  </OrganizerLayout>
</template>
